/**
 * StartSupportSessionDialog.tsx
 * ----------------------------------------------------------------------------
 * Modal para iniciar uma "Sessão de Suporte WL" em um tenant cliente.
 *
 * Usado pelo painel White Label (ex: dentro de WlTenantDetailsModal).
 * Pede ao operador: duração (5–60 min) e motivo (mínimo 5 caracteres,
 * registrado em auditoria). O operador entra com permissões de admin
 * completas dentro do tenant — a segurança é via auditoria + atribuição
 * "Suporte WL" carimbada em cada ação realizada na timeline.
 *
 * RELEASE: 2026-06-12 v2.3.0 - Adiciona seletores "Função" (cargo) e
 *                              "Perfil de Permissão", pré-carregados a partir
 *                              do tenant alvo via RPC wl_get_tenant_support_options.
 *                              Permite ao operador reproduzir o problema sob a
 *                              mesma ótica de permissões do usuário reclamante.
 * RELEASE: 2026-06-12 v2.2.0 - Adiciona toggle "Modo observador" (default ON).
 *                              Valor é propagado para wl_start_support_session
 *                              via parâmetro p_observer_mode e grava
 *                              observer_mode_enabled no tenant_members criado.
 *                              Quando o operador desliga o toggle, exibe
 *                              InlineNotice de advertência reforçando o risco.
 * RELEASE: 2026-05-30 v2.1.0 - Após iniciar sessão, força hard reload para
 *                              "/tenant" com localStorage.currentTenantId =
 *                              p_tenant_id. Isso garante que o AuthProvider
 *                              recarregue availableTenants (que agora inclui
 *                              o tenant alvo via provisionamento temporário
 *                              em wl_start_support_session v2.0.0) e use o
 *                              tenant correto como currentTenant.
 * RELEASE: 2026-05-30 v2.0.1 - Corrige destino do redirecionamento pós-start.
 * RELEASE: 2026-05-30 v2.0.0 - Removido seletor de escopo (read/write).
 * RELEASE: 2026-05-30 v1.0.0 - Versão inicial.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Eye, Loader2, ShieldCheck, UserCog } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { InlineNotice } from "@/components/ui/inline-notice";
import { useToast } from "@/hooks/use-toast";
import {
  useStartSupportSession,
  useTenantSupportOptions,
  type SupportSessionRole,
} from "@/hooks/wl/useSupportSession";
import { describeWlError } from "@/hooks/wl/useWlErrorToast";
import { useNavigate } from "react-router-dom";

/**
 * Helper: salva o tenant alvo em localStorage e força hard reload para "/tenant".
 * Hard reload é necessário porque o operador WL não tinha o tenant alvo em
 * availableTenants — o AuthProvider precisa reinicializar para carregar o
 * vínculo recém-provisionado (tenant_members + user_roles) no banco.
 */
function hardRedirectToTenantApp(tenantId: string) {
  try {
    localStorage.setItem("currentTenantId", tenantId);
  } catch {
    /* ignore storage errors (modo privado etc.) */
  }
  window.location.assign("/tenant");
}

interface Props {
  open: boolean;
  tenantId: string | null;
  tenantName?: string | null;
  onClose: () => void;
}

const DURATIONS = [5, 10, 15, 30, 45, 60];

export function StartSupportSessionDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const start = useStartSupportSession();

  const [duration, setDuration] = useState(15);
  const [reason, setReason] = useState("");
  // v2.2.0: Modo observador ATIVADO por padrão. O operador pode desativar
  // explicitamente caso precise executar ações de escrita no tenant alvo.
  const [observerMode, setObserverMode] = useState(true);
  // v2.3.0: Função (cargo) e Perfil de Permissão escolhidos pelo operador.
  // Defaults preservam o comportamento anterior (admin sem perfil).
  const [role, setRole] = useState<SupportSessionRole>("tenant_admin");
  // Usamos "" como sentinela para "Nenhum" (envia null pro backend).
  const [permissionProfileId, setPermissionProfileId] = useState<string>("");

  // Carrega opções (cargos + perfis ativos) do tenant alvo.
  const optionsQuery = useTenantSupportOptions(tenantId);
  const roleOptions = optionsQuery.data?.roles ?? [];
  const profileOptions = optionsQuery.data?.permission_profiles ?? [];
  const hasProfiles = profileOptions.length > 0;

  const reasonOk = reason.trim().length >= 5 && reason.trim().length <= 500;
  const canSubmit = !!tenantId && reasonOk && !!role && !start.isPending;

  async function handleSubmit() {
    if (!tenantId || !canSubmit) return;
    try {
      await start.mutateAsync({
        tenantId,
        reason: reason.trim(),
        durationMinutes: duration,
        observerMode,
        role,
        permissionProfileId: permissionProfileId ? permissionProfileId : null,
      });
      toast({
        title: "Sessão de suporte iniciada",
        description: `Você está operando como suporte em "${tenantName ?? tenantId}"${
          observerMode ? " (modo observador)" : ""
        }.`,
      });
      // Reset + fecha + hard reload para garantir reidratação total do contexto
      // (availableTenants, roles, permissions, currentTenant) com o novo vínculo.
      setReason("");
      setDuration(15);
      setObserverMode(true);
      setRole("tenant_admin");
      setPermissionProfileId("");
      onClose();
      hardRedirectToTenantApp(tenantId);
    } catch (e: unknown) {
      toast({ variant: "destructive", title: "Falha ao iniciar sessão", description: describeWlError(e) });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !start.isPending && onClose()}>
      <DialogContent className="max-w-md p-0 max-h-[90dvh] flex flex-col gap-0">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Acessar como Suporte
          </DialogTitle>
          <DialogDescription>
            Você abrirá uma sessão temporária dentro do tenant{" "}
            <span className="font-medium text-foreground">
              {tenantName ?? "selecionado"}
            </span>
            . Toda ação fica registrada em auditoria e marcada como
            "Suporte WL" na linha do tempo do cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Duração */}
          <div className="space-y-2">
            <Label htmlFor="duration">Duração</Label>
            <Select
              value={String(duration)}
              onValueChange={(v) => setDuration(Number(v))}
            >
              <SelectTrigger id="duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATIONS.map((m) => (
                  <SelectItem key={m} value={String(m)}>
                    {m} minutos
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              A sessão expira automaticamente ao fim do período.
            </p>
          </div>

          {/* Motivo */}
          <div className="space-y-2">
            <Label htmlFor="reason">
              Motivo do acesso{" "}
              <span className="text-muted-foreground font-normal">
                ({reason.trim().length}/500)
              </span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Ex: chamado #1234 — investigar erro de cobrança recorrente"
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, 500))}
              rows={3}
            />
            {!reasonOk && reason.length > 0 && (
              <p className="text-xs text-destructive">
                O motivo precisa ter pelo menos 5 caracteres.
              </p>
            )}
          </div>

          {/* v2.3.0: Função (cargo) — define o role provisionado no user_roles */}
          <div className="space-y-2">
            <Label
              htmlFor="support-role"
              className="flex items-center gap-2 text-sm font-medium"
            >
              <UserCog className="h-3.5 w-3.5 text-muted-foreground" />
              Função (cargo)
            </Label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as SupportSessionRole)}
              disabled={start.isPending || optionsQuery.isLoading}
            >
              <SelectTrigger id="support-role">
                <SelectValue placeholder="Selecione a função" />
              </SelectTrigger>
              <SelectContent>
                {(roleOptions.length > 0
                  ? roleOptions
                  : [
                      { value: "tenant_admin" as const, label: "Administrador" },
                      { value: "tenant_supervisor" as const, label: "Supervisor" },
                      { value: "agent" as const, label: "Agente" },
                      { value: "user" as const, label: "Usuário" },
                    ]
                ).map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Define o cargo do usuário temporário no tenant alvo, permitindo
              reproduzir o problema com o mesmo nível do reclamante.
            </p>
          </div>

          {/* v2.3.0: Perfil de Permissão — opcional, filtrado por tenant alvo */}
          <div className="space-y-2">
            <Label
              htmlFor="support-profile"
              className="flex items-center gap-2 text-sm font-medium"
            >
              <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
              Perfil de permissão
            </Label>
            <Select
              value={permissionProfileId || "__none__"}
              onValueChange={(v) =>
                setPermissionProfileId(v === "__none__" ? "" : v)
              }
              disabled={
                start.isPending || optionsQuery.isLoading || !hasProfiles
              }
            >
              <SelectTrigger id="support-profile">
                <SelectValue
                  placeholder={
                    optionsQuery.isLoading
                      ? "Carregando perfis..."
                      : hasProfiles
                        ? "Nenhum"
                        : "Sem perfis cadastrados neste tenant"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">
                  Nenhum (sem perfil de permissão)
                </SelectItem>
                {profileOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {hasProfiles
                ? "Pré-carregado a partir dos perfis ativos deste tenant."
                : "Este tenant ainda não possui perfis de permissão ativos."}
            </p>
          </div>


          {/* v2.2.0: Modo Observador (default ON) */}
          <div className="space-y-2 rounded-md border border-border/60 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5">
                <Label
                  htmlFor="observer-mode"
                  className="flex items-center gap-2 text-sm font-medium"
                >
                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                  Modo observador
                </Label>
                <p className="text-xs text-muted-foreground">
                  Recomendado para diagnóstico. Inicia a sessão sem permissão
                  de envio de mensagens ou ações destrutivas no tenant.
                </p>
              </div>
              <Switch
                id="observer-mode"
                checked={observerMode}
                onCheckedChange={setObserverMode}
                disabled={start.isPending}
              />
            </div>
            {!observerMode && (
              <InlineNotice variant="warning" className="mt-2">
                Você entrará com permissões plenas no tenant. Toda ação fica
                registrada em auditoria como "Suporte WL".
              </InlineNotice>
            )}
          </div>
        </div>


        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <Button variant="outline" onClick={onClose} disabled={start.isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {start.isPending && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
            Iniciar sessão
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
